<script setup lang="ts">
import { computed, watch } from "vue";
import isSchemaArray from "../lib/isSchemaArray";
import isSchemaObject from "../lib/isSchemaObject";
import Labels from "../model/labels";
import {
  openSection as globalOpenSection,
  toggleSection as globalToggleSection,
  setOpenSection as setGlobalOpenSection,
  activeSection,
  activeSubSection,
  setActiveSection,
  setActiveSubSection,
} from "../store/Navigation";

const props = defineProps({
  marker: {
    type: Object as any,
    required: true,
  },
  slug: {
    type: String,
    required: true,
  },
});

// Fonction helper pour formater les labels de manière uniforme
const formatLabel = (slug: string): string => {
  let labelsArray: string[] = slug.split("[i]").join("").split(".").slice(1);
  labelsArray = labelsArray.map(elt => Object.keys(Labels).includes(elt) ? Labels[elt] : elt);
  const fullLabel = labelsArray.join(" > ");
  return fullLabel.charAt(0).toUpperCase() + fullLabel.slice(1);
};

const formatSingleKey = (key: string): string => {
  const mapped = Object.keys(Labels).includes(key) ? Labels[key] : key;
  return mapped.charAt(0).toUpperCase() + mapped.slice(1);
};

const anchorLabel = computed(() => formatLabel(props.slug));

const sectionId = computed(() => props.slug);

// Est-ce que cette section est expanded dans la navigation
const isExpanded = computed(() => globalOpenSection.value === sectionId.value);
const isSectionActive = computed(() => activeSection.value === sectionId.value);

watch(
  isSectionActive,
  (newVal) => {
    if (newVal && props.slug.split(".").length === 2) {
      setGlobalOpenSection(sectionId.value);
    }
  },
  { immediate: true }
);

const findElementBySlug = (slug: string): HTMLElement | null => {
  const elements = document.querySelectorAll<HTMLElement>("[data-anchor-slug]");
  for (const el of elements) {
    if (el.dataset.anchorSlug === slug) {
      return el;
    }
  }
  return null;
};

const scrollToSection = (slug: string) => {
  const element = findElementBySlug(slug);
  if (element) {
    const headerOffset = 140;
    const top =
      element.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top, behavior: "smooth" });
  }
};

const handleSectionClick = () => {
  globalToggleSection(sectionId.value);
  setActiveSection(sectionId.value);
  scrollToSection(sectionId.value);
};

const handleLeafSectionClick = () => {
  setActiveSection(sectionId.value);
  scrollToSection(sectionId.value);
};

const handleSubSectionClick = (subSlug: string) => {
  setGlobalOpenSection(sectionId.value);
  setActiveSection(sectionId.value);
  setActiveSubSection(subSlug);
  scrollToSection(subSlug);
};

const buildChildSlug = (parentSlug: string, key: string) => {
  // Ne pas ajouter [i] car les data-anchor-slug dans ListMarkers ne l'incluent pas
  return `${parentSlug}.${key}`;
};

// Vérifier si cette section a des sous-sections
const hasSubsections = computed(() => {
  if (!isSchemaObject(props.marker) || props.slug.split('.').length !== 2) return false;

  for (const key in props.marker.properties) {
    const child = props.marker.properties[key];
    if ((isSchemaObject(child) || isSchemaArray(child)) && !child.description) {
      return true;
    }
  }
  return false;
});

</script>

<template>
  <div v-if="isSchemaObject(marker) || isSchemaArray(marker)" class="h-auto bg-transparent">
    <!-- Section niveau 2 avec collapse -->
    <div v-if="slug.split('.').length === 2">
      <!-- Bouton section avec toggle si a des sous-sections -->
      <button
        v-if="hasSubsections"
        @click="handleSectionClick"
        class="
          w-full
          flex
          items-center
          justify-between
          px-2
          py-1.5
          mb-0.5
          rounded
          text-sm
          transition-colors
          duration-150
          cursor-pointer
        "
        :class="isSectionActive
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-700 dark:hover:text-primary-400'
        "
      >
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <div
            v-if="isSectionActive"
            class="w-1 h-4 bg-primary-600 dark:bg-primary-400 rounded-full flex-shrink-0"
          ></div>
          <span class="truncate">{{ anchorLabel }}</span>
        </div>
        <fa
          :icon="['fas', 'angle-down']"
          class="text-xs transition-transform duration-200"
          :class="{ '-rotate-180': isExpanded }"
        />
      </button>

      <!-- Bouton simple sans sous-sections -->
      <button
        v-else
        @click="handleLeafSectionClick"
        class="
          w-full
          text-left
          block
          cursor-pointer
          px-2
          py-1.5
          mb-0.5
          rounded
          text-sm
          transition-colors
          duration-150
        "
        :class="isSectionActive
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-700 dark:hover:text-primary-400'
        "
      >
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <div
            v-if="isSectionActive"
            class="w-1 h-4 bg-primary-600 dark:bg-primary-400 rounded-full flex-shrink-0"
          ></div>
          <span class="truncate">{{ anchorLabel }}</span>
        </div>
      </button>
    </div>

    <!-- Sous-sections collapsibles -->
    <Transition name="nav-collapse">
      <div v-if="(slug.split('.').length === 2 && isExpanded) || slug.split('.').length < 2">
        <div v-if="isSchemaObject(marker)">
          <template v-for="(child, key) in marker.properties" :key="key">
            <!-- Afficher les sous-sections niveau 3 -->
            <button
              v-if="
                slug.split('.').length === 2 &&
                (isSchemaObject(child) || isSchemaArray(child)) &&
                !child.description
              "
              @click="handleSubSectionClick(buildChildSlug(slug, String(key)))"
              class="
                w-full
                text-left
                block
                pl-6
                pr-2
                py-1
                text-xs
                text-gray-600
                dark:text-gray-400
                hover:text-primary-600
                dark:hover:text-primary-400
                hover:bg-gray-50
                dark:hover:bg-gray-800/50
                rounded
                transition-colors
                duration-150
                cursor-pointer
                min-w-0
                flex
                items-center
              "
              :class="{
                'text-primary-700 dark:text-primary-400 font-semibold bg-primary-50 dark:bg-primary-900/10':
                  activeSubSection === buildChildSlug(slug, String(key)),
              }"
            >
              <span class="mr-1.5 text-gray-400 dark:text-gray-600 flex-shrink-0">›</span>
              <span class="truncate">{{ formatSingleKey(String(key)) }}</span>
            </button>
            <!-- Continuer la récursion pour le reste -->
            <MenuContent
              v-if="slug.split('.').length < 2"
              :slug="slug + '.' + key"
              :marker="child"
            />
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.nav-collapse-enter-active,
.nav-collapse-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.nav-collapse-enter-from,
.nav-collapse-leave-to {
  opacity: 0;
  max-height: 0;
}

.nav-collapse-enter-to,
.nav-collapse-leave-from {
  opacity: 1;
  max-height: 500px;
}
</style>
